import React from 'react';
import { Card } from '../components/ui/card';
import { Users, Heart, Check } from 'lucide-react';

const FamilyMeals = () => {
  const familyMeals = [
    {
      name: 'Chicken + Tenders Family Meal',
      price: '$36.99',
      description: 'The ultimate variety pack for the whole family',
      includes: [
        '6pc Hand-Breaded Chicken',
        '6pc Jumbo Tenders',
        '2 Large Sides',
        '4 Honey Biscuits'
      ],
      servings: '4-6 people',
      badge: 'Most Popular'
    },
    {
      name: '12pc Chicken Family Meal',
      price: '$24.99',
      description: 'Classic fried chicken the whole family loves',
      includes: [
        '12pc Hand-Breaded Chicken',
        '2 Large Sides',
        '4 Honey Biscuits'
      ],
      servings: '4-5 people',
      badge: 'Best Value'
    },
    {
      name: '12 Tenders Family Meal',
      price: '$23.99',
      description: 'Perfect for tender lovers and kids',
      includes: [
        '12pc Jumbo Tenders',
        '2 Large Sides',
        '4 Honey Biscuits'
      ],
      servings: '4-5 people',
      badge: 'Family Favorite'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section 
        className="py-20"
        style={{
          background: `linear-gradient(135deg, var(--marathon-blue) 0%, var(--marathon-blue-dark) 100%)`,
          color: 'var(--white)'
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div 
            className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--marathon-yellow)' }}
          >
            <Users size={40} style={{ color: 'var(--gray-900)' }} />
          </div>
          <h1 className="text-5xl font-extrabold mb-6">Family Meals</h1>
          <p className="text-xl" style={{ color: 'var(--gray-100)' }}>
            Perfect for families, gatherings, and game nights
          </p>
        </div>
      </section>

      {/* Family Meals Grid */}
      <section className="py-16" style={{ backgroundColor: 'var(--gray-50)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--marathon-blue)' }}>
              Feed the Whole Family
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--gray-700)' }}>
              Our family meals are designed to satisfy everyone with generous portions of 
              hand-breaded chicken, tenders, sides, and biscuits.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {familyMeals.map((meal, index) => (
              <Card 
                key={index}
                className="relative p-6 border-0 shadow-xl hover:shadow-2xl"
                style={{ backgroundColor: 'var(--white)' }}
              >
                {/* Badge */}
                {meal.badge && (
                  <div 
                    className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold"
                    style={{ 
                      backgroundColor: 'var(--marathon-yellow)',
                      color: 'var(--gray-900)'
                    }}
                  >
                    {meal.badge}
                  </div>
                )}

                {/* Icon */}
                <div 
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'var(--marathon-blue-light)', color: 'var(--white)' }}
                >
                  <Heart size={32} />
                </div>

                {/* Meal Info */}
                <h3 className="text-xl font-bold text-center mb-2" style={{ color: 'var(--gray-900)' }}>
                  {meal.name}
                </h3>
                <div 
                  className="text-3xl font-extrabold text-center mb-3"
                  style={{ color: 'var(--marathon-blue)' }}
                >
                  {meal.price}
                </div>
                <p className="text-center text-sm mb-4" style={{ color: 'var(--gray-600)' }}>
                  {meal.description}
                </p>

                {/* Servings */}
                <div 
                  className="text-center py-2 px-4 rounded-lg mb-4"
                  style={{ backgroundColor: 'var(--gray-100)' }}
                >
                  <span className="text-sm font-semibold" style={{ color: 'var(--gray-700)' }}>
                    Serves {meal.servings}
                  </span>
                </div>

                {/* Includes */}
                <div className="space-y-2">
                  <p className="text-sm font-semibold mb-2" style={{ color: 'var(--gray-800)' }}>
                    Includes:
                  </p>
                  {meal.includes.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-start space-x-2">
                      <Check 
                        size={18} 
                        className="flex-shrink-0 mt-0.5"
                        style={{ color: 'var(--success-green)' }} 
                      />
                      <span className="text-sm" style={{ color: 'var(--gray-700)' }}>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>

          {/* Side Options Info */}
          <Card className="p-8 border-0 shadow-lg" style={{ backgroundColor: 'var(--white)' }}>
            <h3 className="text-2xl font-bold mb-4 text-center" style={{ color: 'var(--marathon-blue)' }}>
              Choose Your Sides
            </h3>
            <p className="text-center mb-6" style={{ color: 'var(--gray-700)' }}>
              All family meals come with your choice of two large sides from our selection:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {[
                'Mashed Potatoes & Gravy',
                'Mac & Cheese',
                'Jambalaya',
                'Red Beans & Rice',
                'Wedges',
                'Coleslaw'
              ].map((side, index) => (
                <div 
                  key={index}
                  className="flex items-center space-x-2 p-3 rounded-lg"
                  style={{ backgroundColor: 'var(--gray-50)' }}
                >
                  <Check size={18} style={{ color: 'var(--marathon-blue)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--gray-800)' }}>
                    {side}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* Disclaimer */}
          <div className="mt-8 text-center">
            <p className="text-sm italic" style={{ color: 'var(--gray-600)' }}>
              * Prices and availability may vary. Call ahead for large orders or special requests.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: 'var(--white)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--marathon-blue)' }}>
            Planning a Gathering?
          </h2>
          <p className="text-lg mb-8" style={{ color: 'var(--gray-700)' }}>
            Our family meals are perfect for parties, game days, and family dinners.<br />
            Call ahead to place your order and we'll have it ready when you arrive.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:8125465780">
              <button
                className="px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
                style={{
                  backgroundColor: 'var(--marathon-yellow)',
                  color: 'var(--gray-900)'
                }}
              >
                Call to Order: (812) 546-5780
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FamilyMeals;
